import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import Footer from "../common/Footer";
import axios from "axios";
import Swal from "sweetalert2";
import { BarLoader } from "react-spinners";

const modules = {
  toolbar: [
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image", "video"],
    ["clean"],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "link",
  "image",
  "video",
];

const EmailEditor = () => {
  const [body, setBody] = useState("");
  const [email, setEmail] = useState({ subject: "", name: "", to: [], id: "" });
  const [temps, setTemps] = useState([]);
  const [profile, setProfile] = useState(localStorage.getItem("userRole") || "");
  const [emailErrors, setEmailErrors] = useState("");
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [toInputValue, setToInputValue] = useState("");
  const [isEmailFieldTouched, setIsEmailFieldTouched] = useState(false);

  const [loading, setLoading] = useState(false);
  const userToken = localStorage.getItem("userToken");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateEmails = (emailString, showErrors = false) => {
    if (!emailString.trim()) {
      if (showErrors) {
        setEmailErrors("");
        setInvalidEmails([]);
      }
      setEmail({ ...email, to: [] });
      return true;
    }

    const emails = emailString
      .split(",")
      .map((mail) => mail.trim())
      .filter((mail) => mail.length > 0);

    const invalid = emails.filter((mail) => !validateEmail(mail));

    if (invalid.length > 0) {
      if (showErrors) {
        setInvalidEmails(invalid);
        setEmailErrors(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`);
      }
      return false;
    } else {
      if (showErrors) {
        setInvalidEmails([]);
        setEmailErrors("");
      }
      setEmail({ ...email, to: emails });
      return true;
    }
  };

  const handleEmailBlur = () => {
    setIsEmailFieldTouched(true);
    validateEmails(toInputValue, true);
  };

  useEffect(() => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/user`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        const profileType = response?.data?.user?.profileType;
        setProfile(profileType);
        if (profileType) {
          localStorage.setItem("userRole", profileType);
        }
      })
      .catch(() => {});
  }, [userToken]);

  const loadTemps = () => {
    let config = {
      method: "get",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/getalltemps`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    };

    axios
      .request(config)
      .then((response) => {
        setTemps(response.data.templates);
      })
      .catch((error) => {});
  };

  useEffect(() => {
    loadTemps();
  }, []);

  const handleTempSave = async () => {
    setLoading(true);

    let data = JSON.stringify({
      subject: email?.subject,
      body: body,
      name: email?.name,
    });

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/emailtemplate`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        Swal.fire({
          title: "Success!",
          text: "Email Template saved successfully!",
          confirmButtonColor: "#3ca151",
          icon: "success",
        });
        setLoading(false);
      })
      .catch((error) => {
        Swal.fire({
          title: "Error in saving email",
          text: "Please Try Again",
          confirmButtonColor: "#3ca151",
          icon: "error",
          confirmButtonText: "Okay",
        });
        setLoading(false);
      });
  };

  const handleSubmit = async () => {
    setIsEmailFieldTouched(true);
    if (!validateEmails(toInputValue, true)) {
      Swal.fire({
        title: "Invalid Email Addresses",
        text: emailErrors,
        confirmButtonColor: "#3ca151",
        icon: "error",
        confirmButtonText: "Okay",
      });
      return;
    }

    if (email?.to.length === 0) {
      Swal.fire({
        title: "No Recipients",
        text: "Please enter at least one email address",
        confirmButtonColor: "#3ca151",
        icon: "warning",
        confirmButtonText: "Okay",
      });
      return;
    }

    setLoading(true);

    let data = JSON.stringify({
      to: email?.to,
      subject: email?.subject,
      body: body,
      name: email?.name,
    });

    let config = {
      method: "post",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/sendmail`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then(async (response) => {
        await Swal.fire({
          title: "Success!",
          text: "Email sent successfully!",
          confirmButtonColor: "#3ca151",
          icon: "success",
        });
        setLoading(false);
        window.location.reload();
      })
      .catch(async (error) => {
        await Swal.fire({
          title: "Error in sending email",
          text: "Please Try Again",
          confirmButtonColor: "#3ca151",
          icon: "error",
          confirmButtonText: "Okay",
        });
        setLoading(false);
        window.location.reload();
      });
  };

  const handleUpdate = async () => {
    setLoading(true);

    let data = JSON.stringify({
      id: email?.id,
      subject: email?.subject,
      body: body,
      name: email?.name,
    });

    let config = {
      method: "patch",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/emailtemplate`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then(async (response) => {
        await Swal.fire({
          title: "Success!",
          text: "Email Template updated successfully!",
          confirmButtonColor: "#3ca151",
          icon: "success",
        });
        setLoading(false);
        window.location.reload();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error in saving email",
          text: "Please Try Again",
          confirmButtonColor: "#3ca151",
          icon: "error",
          confirmButtonText: "Okay",
        });
        setLoading(false);
      });
  };

  const handleDelete = async () => {
    setLoading(true);

    let data = JSON.stringify({
      tempId: email?.id,
    });

    let config = {
      method: "delete",
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_LOCAL_API}/emailtemplate`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      data: data,
    };

    axios
      .request(config)
      .then(async(response) => {
       await  Swal.fire({
          title: "Success!",
          text: "Email Template deleted successfully!",
          confirmButtonColor: "#3ca151",
          icon: "success",
        });
        setLoading(false);
        window.location.reload();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error in deleting email",
          text: "Please Try Again",
          confirmButtonColor: "#3ca151",
          icon: "error",
          confirmButtonText: "Okay",
        });
        setLoading(false);
      });
  };

  return (
    <>
      <div className="email-editor-wrapper" style={{ width: "95%", margin: "50px auto", paddingBottom: "20px" }}>
        <h1 id="head_dash">Email Editor</h1>
        <div style={{ marginBottom: "5px" }}>
          <select
            disabled={profile === "view_access"}
            onChange={(e) => {
              const t = JSON.parse(e.target.value);
              setEmail({
                ...email,
                subject: t?.subject,
                name: t?.name,
                id: t?._id,
              });
              setBody(t?.body);
              setToInputValue("");
              setEmailErrors("");
              setInvalidEmails([]);
              setIsEmailFieldTouched(false);
            }}
          >
            <option value={JSON.stringify({ subject: "", name: "", _id: "" })}>
              New Template
            </option>
            {temps.map((m) => (
              <option value={JSON.stringify(m)}>{m.name}</option>
            ))}
          </select>

          {loading && <BarLoader color="#3ca151" />}
          <br />
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Template Name:
            <input
              type="text"
              value={email ? email.name : ""}
              disabled={profile === "view_access"}
              onChange={(e) => {
                setEmail({ ...email, name: e.target.value });
              }}
              style={{
                width: "100%",
                padding: "10px",
                boxSizing: "border-box",
                marginTop: "5px",
                marginBottom: "0px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
              placeholder="Enter name of the template"
            />
          </label>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            To:
            <input
              type="text"
              value={toInputValue}
              disabled={profile === "view_access"}
              onChange={(e) => {
                setToInputValue(e.target.value);
                validateEmails(e.target.value, false);
              }}
              onBlur={handleEmailBlur}
              style={{
                width: "100%",
                padding: "10px",
                boxSizing: "border-box",
                marginTop: "5px",
                marginBottom: "0px",
                borderRadius: "4px",
                border: isEmailFieldTouched && invalidEmails.length > 0 ? "2px solid #ff4444" : "1px solid #ccc",
                fontSize: "16px",
              }}
              placeholder="Enter email addresses separated by commas"
            />
            {isEmailFieldTouched && emailErrors && (
              <div
                style={{
                  color: "#ff4444",
                  fontSize: "14px",
                  marginTop: "5px",
                  marginBottom: "10px",
                }}
              >
                {emailErrors}
              </div>
            )}
          </label>
          <label
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Email Subject:
            <input
              type="text"
              value={email.subject}
              disabled={profile === "view_access"}
              onChange={(e) => setEmail({ ...email, subject: e.target.value })}
              style={{
                width: "100%",
                padding: "10px",
                boxSizing: "border-box",
                marginTop: "5px",
                marginBottom: "0px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "16px",
              }}
              placeholder="Email subject"
            />
          </label>
        </div>
        <div style={{ marginBottom: "10px", marginTop: "0px" }}>
          <span
            style={{
              display: "block",
              marginBottom: "5px",
              fontWeight: "bold",
            }}
          >
            Body:
          </span>
          <ReactQuill
            theme="snow"
            value={body}
            onChange={setBody}
            readOnly={profile === "view_access"}
            modules={profile === "view_access" ? { toolbar: false } : modules}
            formats={formats}
            style={{ height: "300px", marginBottom: "40px" }}
          />
        </div>
        {profile !== "view_access" && (
          <>
        <button
          onClick={handleSubmit}
          style={{
            padding: "10px 20px",
            margin: "20px 5px",
            fontSize: "16px",
          }}
        >
          Send
        </button>

        {email.id === "" && (
          <button
            onClick={handleTempSave}
            style={{
              padding: "10px 20px",
              margin: "20px 5px",
              fontSize: "16px",
            }}
          >
            Save as Template
          </button>
        )}
        {email?.id && (
          <>
            <button
              onClick={handleUpdate}
              style={{
                padding: "10px 20px",
                margin: "20px 5px",
                fontSize: "16px",
              }}
            >
              Update Template
            </button>
            <button
              onClick={handleDelete}
              style={{
                padding: "10px 20px",
                margin: "20px 5px",
                fontSize: "16px",
                backgroundColor:"red"
              }}
            >
              Delete Template
            </button>
          </>
            )}
          </>
        )}
        {profile === "view_access" && (
          <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
            <p>View Access: You can view email templates but cannot send, create, update, or delete them.</p>
          </div>
        )}
      </div>
      <Footer />{" "}
    </>
  );
};

export default EmailEditor;
